<template>
  <div class="message-wrapper" :class="message.role">
    <div class="message-bubble">
      <!-- Edit Mode (User only) -->
      <div v-if="isEditing" class="edit-mode">
        <textarea v-model="editContent"></textarea>
         <div class="edit-actions">
            <button type="button" @click="saveEdit">Save & Submit</button>
            <button type="button" @click="isEditing = false" class="cancel">Cancel</button>
         </div>
      </div>
      
      <!-- Display Mode -->
      <div v-else>
        <div v-if="message.role === 'ai'" class="message-text markdown-content" v-html="renderedMarkdown"></div>
        <div v-else class="message-text">{{ message.text }}</div>
      </div>
    </div>
    
    <!-- Sources (AI messages only) -->
    <div v-if="message.role === 'ai' && normalizedSources.length > 0" class="sources-section">
        <span class="sources-label">Sources:</span>
        <a v-for="source in normalizedSources"
           :key="source.topicId"
           :href="topicUrl(source.topicId)"
           class="source-link">
            {{ source.title || ('Topic #' + source.topicId) }}
        </a>
    </div>
    
    <!-- Controls (Versions & Actions) -->
    <div class="message-footer">
        <!-- Navigation Arrows -->
        <div class="nav-arrows" v-if="nav && nav.total > 1">
            <span class="nav-btn" @click="switchPrev">
                <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none"><polyline points="15 18 9 12 15 6"></polyline></svg>
            </span>
            <span class="nav-info">{{ nav.current }} / {{ nav.total }}</span>
            <span class="nav-btn" @click="switchNext">
                <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none"><polyline points="9 18 15 12 9 6"></polyline></svg>
            </span>
        </div>
        
        <!-- Actions -->
        <div class="actions">
            <span v-if="message.role === 'user'" @click="startEdit" title="Edit">
                <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
            </span>
            <span v-if="message.role === 'ai'" @click="reroll" title="Regenerate">
                <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
            </span>
        </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

export default {
  name: 'ChatMessage',
  props: {
    message: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
        isEditing: false,
        editContent: ''
    };
  },
  computed: {
      ...mapGetters('chatStore', ['getNodeNavigation']),
      nav() {
          return this.getNodeNavigation(this.message.id);
      },
      normalizedSources() {
          if (!this.message.sources) return [];
          return this.message.sources.map(s => {
              // Case 1: Plain integer (old localStorage format)
              if (typeof s === 'number') {
                  return { topicId: s, title: null };
              }
              // Case 2: String — could be stringified JSON or a plain string
              if (typeof s === 'string') {
                  try {
                      const parsed = JSON.parse(s);
                      if (parsed && typeof parsed === 'object') {
                          return {
                              topicId: parsed.topicId || parsed.TopicId || null,
                              title: parsed.title || parsed.Title || null
                          };
                      }
                  } catch (e) {
                      // Not valid JSON — try to extract a topic ID from the string
                      const match = s.match(/(\d+)/);
                      return { topicId: match ? parseInt(match[1], 10) : null, title: s };
                  }
              }
              // Case 3: Object — normalize property casing (handle both camelCase and PascalCase)
              if (typeof s === 'object' && s !== null) {
                  return {
                      topicId: s.topicId || s.TopicId || null,
                      title: s.title || s.Title || null
                  };
              }
              // Case 4: Unexpected type — skip
              return null;
          }).filter(s => s !== null && s.topicId !== null);
      },
      renderedMarkdown() {
          if (!this.message.text) return '';
          let html = marked.parse(this.message.text, { breaks: true, gfm: true });
          // Prepend ApplicationPath to topic links generated by AI
          let basePath = (window.ApplicationPath || '').replace(/\/+$/, '');
          if (basePath) {
              html = html.replace(/href="\/Views\/TopicRouter\.aspx/g, 'href="' + basePath + '/Views/TopicRouter.aspx');
          }
          // Sanitize: LLM/RAG output is untrusted (prompt injection, poisoned topic bodies).
          // Without this, raw <script>, <img onerror>, and inline handlers persist into UserSetting
          // and re-render every time the chat is reopened (stored XSS).
          return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
      }
  },
  methods: {
      ...mapActions('chatStore', ['switchVersion', 'sendMessage', 'rerollResponse', 'jumpToMessage']),

      topicUrl(topicId) {
          let basePath = (window.ApplicationPath || '').replace(/\/+$/, '');
          return basePath + '/Views/TopicRouter.aspx?action=view&topicId=' + topicId;
      },

      switchPrev() {
          this.switchVersion({ nodeId: this.message.id, direction: -1 });
      },
      switchNext() {
          this.switchVersion({ nodeId: this.message.id, direction: 1 });
      },
      
      startEdit() {
          this.editContent = this.message.text;
          this.isEditing = true;
      },
      
      saveEdit() {
          if (!this.editContent.trim() || this.editContent === this.message.text) {
              this.isEditing = false;
              return;
          }
          
          // To "edit", we jump to the parent and send a new message
          if (this.message.parentId) {
              this.jumpToMessage(this.message.parentId);
              this.sendMessage(this.editContent);
          } else {
             // For root edit, we reset head to start fresh branch from root
             this.jumpToMessage(null);
             this.sendMessage(this.editContent); 
          }
          this.isEditing = false;
      },
      
      reroll() {
          if (this.message.parentId) {
              this.rerollResponse(this.message.parentId);
          }
      }
  }
};
</script>

<style scoped>
.message-wrapper {
    margin-bottom: 15px;
    display: flex;
    flex-direction: column;
}

.message-wrapper.user {
    align-items: flex-end;
}

.message-wrapper.ai {
    align-items: flex-start;
}

.message-bubble {
    max-width: 85%;
    padding: 10px 14px;
    border-radius: 12px;
    position: relative;
    font-size: 14px;
    line-height: 1.4;
}

.user .message-bubble {
    background-color: var(--tile-bg-color);
    color: white;
    border-bottom-right-radius: 2px;
}

.ai .message-bubble {
    background-color: white;
    color: #323130;
    border: 1px solid #e1dfdd;
    border-bottom-left-radius: 2px;
}

.sources-section {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    padding: 6px 10px;
    background: #f5f5f5;
    border-radius: 8px;
    font-size: 11px;
    max-width: 85%;
}

.sources-label {
    color: #605e5c;
    font-weight: 500;
}

.source-link {
    color: var(--main-color-1, #304897);
    text-decoration: none;
    padding: 2px 6px;
    background: white;
    border: 1px solid #e1dfdd;
    border-radius: 4px;
    transition: all 0.15s ease;
}

.source-link:hover {
    background: var(--main-color-1, #304897);
    color: white;
    border-color: var(--main-color-1, #304897);
}

.message-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 85%;
    margin-top: 2px;
    font-size: 11px;
    color: #605e5c;
    height: 16px;
}

.actions span, .nav-btn {
    cursor: pointer;
    margin: 0 4px;
    padding: 2px;
}

.actions span:hover, .nav-btn:hover {
    color: var(--tile-bg-color);
    background: #f3f2f1;
    border-radius: 4px;
}

.nav-arrows {
    display: flex;
    align-items: center;
    user-select: none;
}

.edit-mode textarea {
    width: 100%;
    min-width: 200px;
    border-radius: 4px;
    border: 1px solid white;
    color: black;
    padding: 4px;
}

.edit-actions {
    margin-top: 5px;
    display: flex;
    gap: 5px;
}

.edit-actions button {
    font-size: 11px;
    padding: 2px 6px;
    border: none;
    background: rgba(255,255,255,0.2);
    color: white;
    cursor: pointer;
}

.ai .edit-actions button {
    background: var(--tile-bg-color);
}

.edit-actions button.cancel {
    background: transparent;
}

/* Fix alignment for user message actions */
.message-wrapper.user .message-footer {
    justify-content: flex-end;
    gap: 8px;
}

/* Markdown content styling */
.markdown-content :deep(p) {
    margin: 0 0 0.5em 0;
}

.markdown-content :deep(p:last-child) {
    margin-bottom: 0;
}

.markdown-content :deep(h1),
.markdown-content :deep(h2),
.markdown-content :deep(h3),
.markdown-content :deep(h4),
.markdown-content :deep(h5),
.markdown-content :deep(h6) {
    margin: 0.5em 0 0.3em 0;
    font-weight: 600;
    line-height: 1.3;
}

.markdown-content :deep(h1) { font-size: 1.4em; }
.markdown-content :deep(h2) { font-size: 1.25em; }
.markdown-content :deep(h3) { font-size: 1.1em; }

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
    margin: 0.3em 0;
    padding-left: 1.5em;
}

.markdown-content :deep(li) {
    margin: 0.2em 0;
}

.markdown-content :deep(code) {
    background: rgba(0, 0, 0, 0.08);
    padding: 0.15em 0.4em;
    border-radius: 3px;
    font-family: 'Consolas', 'Monaco', monospace;
    font-size: 0.9em;
}

.markdown-content :deep(pre) {
    background: #1e1e1e;
    color: #d4d4d4;
    padding: 0.8em;
    border-radius: 6px;
    overflow-x: auto;
    margin: 0.5em 0;
}

.markdown-content :deep(pre code) {
    background: none;
    padding: 0;
    color: inherit;
}

.markdown-content :deep(blockquote) {
    border-left: 3px solid var(--main-color-1, #304897);
    margin: 0.5em 0;
    padding-left: 0.8em;
    color: #666;
    font-style: italic;
}

.markdown-content :deep(a) {
    color: var(--main-color-1, #304897);
    text-decoration: underline;
}

.markdown-content :deep(a:hover) {
    opacity: 0.8;
}

.markdown-content :deep(strong) {
    font-weight: 600;
}

.markdown-content :deep(table) {
    border-collapse: collapse;
    margin: 0.5em 0;
    width: 100%;
}

.markdown-content :deep(th),
.markdown-content :deep(td) {
    border: 1px solid #ddd;
    padding: 0.4em 0.6em;
    text-align: left;
}

.markdown-content :deep(th) {
    background: #f5f5f5;
    font-weight: 600;
}

.markdown-content :deep(hr) {
    border: none;
    border-top: 1px solid #e1dfdd;
    margin: 0.8em 0;
}
</style>
